import {
  Button,
  Flex,
  Form,
  Heading,
  Modal,
  Paragraph,
  Skeleton,
  useToast,
} from "@ir/ui";
import { PageHeader } from "@src/presentation/components/Layout/common";
import { type BreadcrumbItem } from "@src/presentation/components/common";
import { useMemo, useState } from "react";
import { useOrganizationsData } from "@src/presentation/providers/OrganizationsDataProvider";
import type { Organization } from "@src/domain/entities/organization";
import { Organization as OrganizationEntity } from "@src/domain/entities/organization";
import { normalizeOrganizationLogoFile } from "@src/domain/dto/organizations";
import type { Brand } from "@src/domain/entities/brand";
import { BrandsAddEditForm, type BrandFormValues } from "./BrandsAddEditForm";
import {
  OrganizationsAddEditForm,
  type OrganizationFormValues,
} from "./OrganizationsAddEditForm";
import { useUpdateOrganization } from "./useUpdateOrganization";
import type { UpdateOrganizationPayload } from "./useUpdateOrganization";
import { useUpdateBrand } from "./useUpdateBrand";
import { normalizeBrandLogoFile } from "@src/domain/dto/brands/UpdateBrand.dto";
import {
  isBrandKeyDuplicateError,
  BRAND_KEY_ALREADY_EXISTS_MESSAGE,
} from "./brandKeyValidation";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "";
};

function buildUpdateOrganizationPayload(
  base: UpdateOrganizationPayload,
  newLogoFile: File | null | undefined,
  removeLogo: boolean,
): UpdateOrganizationPayload {
  if (newLogoFile) {
    return { ...base, organizationLogoFile: newLogoFile };
  }
  if (removeLogo) {
    return { ...base, organizationLogoRemove: true };
  }
  return base;
}

/**
 * REST save payloads may omit logo or brands; align client state with intent after save.
 */
function clientOrganizationAfterSave(
  previous: Organization | null,
  updated: Organization,
  opts: { logoRemoved: boolean; newFileUploaded: boolean },
): Organization {
  const prev = previous;
  const logoUrl = (() => {
    if (opts.logoRemoved && !opts.newFileUploaded) return null;
    if (opts.newFileUploaded) return updated.organizationLogoUrl;
    const fromApi = updated.organizationLogoUrl?.trim() ?? "";
    if (fromApi.length > 0) return updated.organizationLogoUrl;
    return prev?.organizationLogoUrl ?? null;
  })();
  const brands =
    updated.brands.length > 0 ? updated.brands : (prev?.brands ?? []);

  return new OrganizationEntity({
    organizationId: updated.organizationId,
    organizationKey: updated.organizationKey,
    organizationName: updated.organizationName,
    organizationDisplayName: updated.organizationDisplayName,
    organizationDescription: updated.organizationDescription,
    organizationLogoUrl: logoUrl,
    organizationIsActive: updated.organizationIsActive,
    organizationCreatedAt:
      updated.organizationCreatedAt ||
      prev?.organizationCreatedAt ||
      new Date().toISOString(),
    organizationUpdatedAt:
      updated.organizationUpdatedAt ?? prev?.organizationUpdatedAt ?? null,
    organizationDeletedAt:
      updated.organizationDeletedAt ?? prev?.organizationDeletedAt ?? null,
    organizationCreatedBy: updated.organizationCreatedBy,
    organizationUpdatedBy:
      updated.organizationUpdatedBy ?? prev?.organizationUpdatedBy ?? null,
    organizationDeletedBy:
      updated.organizationDeletedBy ?? prev?.organizationDeletedBy ?? null,
    brands,
  });
}

export interface OrganizationDetailsPageHeaderProps {
  /** Current organization entity (null while loading). */
  organization: Organization | null;
  /** Title shown in page header (e.g. display name fallback). */
  headerTitle: string;
  subtitle: string;
  error: string | null;
  organizationDetailsLoading: boolean;
  breadcrumbItems: BreadcrumbItem[];
  onOrganizationSaved?: (organization: Organization) => Promise<void> | void;
  onBrandSaved?: (brand: Brand) => Promise<void> | void;
}

export const OrganizationDetailsPageHeader = ({
  organization,
  headerTitle,
  subtitle,
  error,
  organizationDetailsLoading,
  breadcrumbItems,
  onOrganizationSaved,
  onBrandSaved,
}: OrganizationDetailsPageHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBrandSaving, setIsBrandSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [isBrandFormSubmittable, setIsBrandFormSubmittable] = useState(false);
  const [form] = Form.useForm<OrganizationFormValues>();
  const [brandForm] = Form.useForm<BrandFormValues>();
  const updateOrganization = useUpdateOrganization();
  const updateBrand = useUpdateBrand();
  const { success: successToast, error: errorToast } = useToast();
  const { data: organizationsListData, getKnownBrandKeys } =
    useOrganizationsData();

  const existingBrandKeys = useMemo(
    () => getKnownBrandKeys(),
    [getKnownBrandKeys, organizationsListData],
  );

  const addBrandInitialValues = useMemo<BrandFormValues>(
    () => ({
      brandId: null,
      organizationId: organization?.organizationId ?? 0,
      organizationKey: organization?.organizationKey ?? "",
      brandKey: "",
      brandName: "",
      brandDisplayName: "",
      brandDescription: "",
      brandLogoFile: null,
      removeBrandLogo: false,
      brandIsActive: true,
    }),
    [organization?.organizationId, organization?.organizationKey],
  );

  const initialValues = useMemo<OrganizationFormValues>(
    () => ({
      organizationId: organization?.organizationId ?? null,
      organizationKey: organization?.organizationKey ?? "",
      organizationName: organization?.organizationName ?? "",
      organizationDisplayName:
        organization?.organizationDisplayName ??
        organization?.organizationName ??
        "",
      organizationDescription: organization?.organizationDescription ?? "",
      organizationLogoFile: null,
      removeOrganizationLogo: false,
      organizationIsActive: organization?.organizationIsActive ?? true,
    }),
    [organization],
  );

  const handleSubmit = async (values: OrganizationFormValues) => {
    setIsSaving(true);
    try {
      const newLogoFile = normalizeOrganizationLogoFile(
        values.organizationLogoFile,
      );
      const removeLogo = Boolean(
        values.removeOrganizationLogo ??
        form.getFieldValue("removeOrganizationLogo"),
      );

      const base: UpdateOrganizationPayload = {
        organizationId: organization?.organizationId ?? null,
        organizationKey: values.organizationKey?.trim() ?? "",
        organizationName: values.organizationName?.trim() ?? "",
        organizationDisplayName: values.organizationDisplayName?.trim() ?? "",
        organizationDescription: values.organizationDescription?.trim() || null,
        organizationIsActive: Boolean(values.organizationIsActive),
      };

      const payload = buildUpdateOrganizationPayload(
        base,
        newLogoFile,
        removeLogo,
      );

      console.log("[Organization edit] before submit", {
        formValues: values,
        payload,
      });

      const updated = await updateOrganization(payload);
      const next = clientOrganizationAfterSave(organization, updated, {
        logoRemoved: removeLogo,
        newFileUploaded: Boolean(newLogoFile),
      });
      await onOrganizationSaved?.(next);
      successToast({ content: "Organization updated successfully." });
      setIsFormSubmittable(false);
      setIsModalOpen(false);
    } catch (saveError) {
      console.error("Failed to update organization", saveError);
      const parsedErrorMessage = getErrorMessage(saveError);
      const errorMessage = parsedErrorMessage.toLowerCase();
      if (
        errorMessage.includes("duplicate") ||
        errorMessage.includes("already exists")
      ) {
        form.setFields([
          {
            name: "organizationKey",
            errors: ["Organization key already exists"],
          },
        ]);
      }
      errorToast({
        content: "Failed to update organization. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddBrandSubmit = async (values: BrandFormValues) => {
    if (!organization?.organizationId) return;

    const brandKey = values.brandKey?.trim().toUpperCase();
    if (brandKey && existingBrandKeys.has(brandKey)) {
      brandForm.setFields([
        {
          name: "brandKey",
          errors: [BRAND_KEY_ALREADY_EXISTS_MESSAGE],
        },
      ]);
      setIsBrandFormSubmittable(false);
      return;
    }

    setIsBrandSaving(true);
    try {
      const logoFile = normalizeBrandLogoFile(values.brandLogoFile);
      const saved = await updateBrand({
        brandId: null,
        organizationId: organization.organizationId,
        organizationKey: organization.organizationKey,
        brandKey: values.brandKey?.trim() ?? "",
        brandName: values.brandName?.trim() ?? "",
        brandDisplayName: values.brandDisplayName?.trim() ?? "",
        brandDescription: values.brandDescription?.trim() || null,
        brandIsActive: Boolean(values.brandIsActive),
        ...(logoFile ? { brandLogoFile: logoFile } : {}),
      });
      await onBrandSaved?.(saved);
      successToast({ content: "Brand saved successfully." });
      setIsAddBrandModalOpen(false);
      brandForm.resetFields();
      setIsBrandFormSubmittable(false);
    } catch (saveError: unknown) {
      console.error("Failed to save brand", saveError);
      if (isBrandKeyDuplicateError(saveError)) {
        brandForm.setFields([
          {
            name: "brandKey",
            errors: [BRAND_KEY_ALREADY_EXISTS_MESSAGE],
          },
        ]);
        setIsBrandFormSubmittable(false);
      }
      errorToast({
        content: "Failed to save brand. Please try again.",
      });
    } finally {
      setIsBrandSaving(false);
    }
  };

  return (
    <>
      <PageHeader breadcrumb={{ items: breadcrumbItems }}>
        <Flex align="center" gap={0.5} justifyContent="space-between">
          <Flex align="center" gap={0.5}>
            <Flex direction="column">
              <Heading level={5} className="text-grey-700 font-bold m-0">
                {organizationDetailsLoading ? (
                  <Skeleton
                    height={24}
                    width={400}
                    animation="wave"
                    variant="rounded"
                  />
                ) : (
                  headerTitle || "Organization"
                )}
              </Heading>
              <Paragraph className="text-grey-500 font-regular m-0">
                {subtitle}
              </Paragraph>
              {error && (
                <Paragraph className="text-danger font-regular m-0">
                  {error}
                </Paragraph>
              )}
            </Flex>
          </Flex>
          <Flex gap={0.5}>
            <HasAnyPermission requiredPermissions={[Permissions.BRANDS_ADD]}>
              <Button
                size="small"
                variant="outline"
                onPress={() => {
                  setIsBrandFormSubmittable(false);
                  brandForm.resetFields();
                  brandForm.setFieldsValue(addBrandInitialValues);
                  setIsAddBrandModalOpen(true);
                }}
                isDisabled={
                  organizationDetailsLoading || !organization?.organizationId
                }
              >
                Add Brand
              </Button>
            </HasAnyPermission>
            <HasAnyPermission requiredPermissions={[Permissions.ORGANIZATIONS_EDIT]}>
            <Button
              size="small"
              variant="outline"
              onPress={() => {
                setIsFormSubmittable(false);
                setIsModalOpen(true);
              }}
              isDisabled={
                organizationDetailsLoading || !organization?.organizationId
              }
            >
                Edit Organization
              </Button>
            </HasAnyPermission>
          </Flex>
        </Flex>
      </PageHeader>
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsFormSubmittable(false);
        }}
        size="large"
        classNames={{
          body: "organization-details-page-header-modal-content",
          header: "organization-details-page-header-modal-header",
        }}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Edit Organization
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              size="small"
              onPress={() => setIsModalOpen(false)}
              color="grey"
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <OrganizationsAddEditForm
          form={form}
          mode="edit"
          initialValues={initialValues}
          existingLogoUrl={organization?.organizationLogoUrl ?? null}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </Modal>
      <Modal
        isOpen={isAddBrandModalOpen}
        onClose={() => {
          setIsAddBrandModalOpen(false);
          setIsBrandFormSubmittable(false);
        }}
        size="large"
        classNames={{
          body: "organization-details-page-header-modal-content",
          header: "organization-details-page-header-modal-header",
        }}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Add Brand
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              size="small"
              onPress={() => setIsAddBrandModalOpen(false)}
              color="grey"
              isDisabled={isBrandSaving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => brandForm.submit()}
              isLoading={isBrandSaving}
              isDisabled={isBrandSaving || !isBrandFormSubmittable}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <BrandsAddEditForm
          form={brandForm}
          mode="create"
          initialValues={addBrandInitialValues}
          existingBrandKeys={existingBrandKeys}
          onSubmit={handleAddBrandSubmit}
          onFormValidityChange={setIsBrandFormSubmittable}
        />
      </Modal>
    </>
  );
};
